import pegasus from "../../assets/scripts/global/01-pegasus";

export default class Animation {
    visible = false;
    playing = false;
    mouseX = 0;
    mouseY = 0;
    translateX = 0;
    translateY = 0;
    debounceTimeout = null;
    parallaxStrength = 50;
    runWhenInView = false;
    raf = null;
    isParallax = false;
    isEntrance = false;
    isSvg = false;
    isInteraction = false;
    parallaxSpeed = 0.1;
    parallaxY = 0;
    targetParallaxY = 0;
    wrappers = {
        main: null,
        parallax: null,
        entrance: null,
        interaction: null,
        svg: null,
        content: null
    };
    
    /**
     * Initializes animations based on selected types
     * @param {object} options - Options object
     */
    initAnimation(options = {}) {

        // Break if no animations are provided
        if (!options?.animations) return;

        // Parse animation types
        if (options?.animationTypes?.includes(',')) {
            this.animationTypes = options?.animationTypes.split(',').map(type => type.trim());
        } else if (options?.animationTypes) {
            this.animationTypes = [options?.animationTypes];
        } else {
            this.animationTypes = [];
        }

        // Parse animations list
        if (options?.animations?.includes(',')) {
            this.animations = options?.animations.split(',').map(animation => animation.trim());
        } else if (options?.animations?.includes('[')) {
            this.animations = JSON.parse(options?.animations);
        } else {
            this.animations = [options?.animations];
        }

        this.looping = options?.looping;
        this.visible = !Boolean(options?.runWhenInView);
        this.animationDelay = options?.animationDelay;
        
        // Set animation type flags
        this.isParallax = this.animationTypes.includes('parallax');
        this.isEntrance = this.animationTypes.includes('entrance');
        this.isSvg = this.animationTypes.includes('svg');
        this.isInteraction = this.animationTypes.includes('interaction');

        // Initialize wrappers
        this.initWrappers();
        
        // Initialize each animation type if present
        if (this.isParallax) {
            this.initParallaxEffects();
        }
        
        if (this.isInteraction) {
            this.initInteractionEffects();
        }
        
        if (this.isSvg) {
            this.initSvgEffects();
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'));
            });
        });
    }
    
    /**
     * Initialize all wrapper elements
     */
    initWrappers() {
        // Initialize main wrapper
        this.wrappers.main = this.$el.querySelector('.Animation__wrapper');
        
        // Get specialized wrappers if they exist
        if (this.isParallax) {
            this.wrappers.parallax = this.$el.querySelector('.Animation__parallax-wrapper');
            this.parallaxSpeed = parseFloat(this.$el.dataset.parallaxSpeed) || 0.1;
        }
        
        if (this.isEntrance) {
            this.wrappers.entrance = this.$el.querySelector('.Animation__entrance-wrapper');
        }
        
        if (this.isInteraction) {
            this.wrappers.interaction = this.$el.querySelector('.Animation__interaction-wrapper');
        }
        
        if (this.isSvg) {
            this.wrappers.svg = this.$el.querySelector('.Animation__svg-wrapper');
        }
        
        // Content wrapper is always present
        this.wrappers.content = this.$el.querySelector('.Animation__content');
        
        // Handle image loading
        this.ensureImagesLoaded();
    }
    
    /**
     * Make sure all images are properly loaded
     */
    ensureImagesLoaded() {
        // Find all images in the block
        const images = this.$el.querySelectorAll('img');
        
        if (images.length > 0) {
            images.forEach(img => {
                // Check if image is already loaded
                if (img.complete) {
                    this.handleImageLoaded(img);
                } else {
                    // Add event listener for when image loads
                    img.addEventListener('load', () => this.handleImageLoaded(img));
                    
                    // Handle error case
                    img.addEventListener('error', () => {
                        console.warn('Failed to load image in Animation block:', img.src);
                    });
                }
                
                // For lazy-loaded images
                if (img.classList.contains('lazy')) {
                    const actualSrc = img.dataset.src;
                    if (actualSrc && img.src !== actualSrc) {
                        img.src = actualSrc;
                    }
                }
            });
        }
    }
    
    /**
     * Handle successfully loaded images
     */
    handleImageLoaded(img) {
        // Make sure image is visible
        img.style.opacity = '1';
        
        // Force redraw to prevent flickering
        if (this.isParallax) {
            requestAnimationFrame(() => {
                if (this.wrappers.parallax) {
                    this.wrappers.parallax.style.transform = this.wrappers.parallax.style.transform;
                }
            });
        }
    }
    
    /**
     * Initialize all parallax-related animations
     */
    initParallaxEffects() {        
        if (this.animations.some(anim => ['parallax-object', 'parallax-background'].includes(anim))) {
            this.initParallax();
        }
    }
    
    /**
     * Initialize all interaction-related animations
     */
    initInteractionEffects() {        
        if (this.animations.includes('cursor-repel')) {
            this.initMouseTracking();
        }
    }
    
    /**
     * Initialize all SVG-related animations
     */
    initSvgEffects() {        
        // SVG animations are mostly handled by CSS, but any JS-specific
        // initialization could be added here. ToDo: OD add SVG-specific logic if needed.
    }

    /**
     * Get animation state classes
     */
    get animationClasses() {
        const classes = [];
        
        if (this.visible) {
            classes.push('is-visible');
        }
        
        if (this.playing) {
            classes.push('is-playing');
        }
        
        if (this.looping) {
            classes.push('is-looping');
        }

        return classes.join(' ');
    }

    /**
     * Initialize parallax effect with IntersectionObserver
     */
    initParallax() {
        const isMobile = pegasus.breakpoints.isMax('sm');
        const rootMargin = isMobile ? '50px' : '100px';
        
        // Simplify thresholds for better mobile performance
        const thresholds = isMobile 
            ? [0, 0.25, 0.5, 0.75, 1] 
            : [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
        
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (!this.raf) {
                        this.startParallax();
                    }
                } else {
                    this.stopParallax();
                }
            });
        }, {
            threshold: thresholds,
            rootMargin: rootMargin
        });

        observer.observe(this.$el);
    }

    /**
     * Start parallax animation loop
     */
    startParallax() {        
        const rect = this.$el.getBoundingClientRect();
        const scrolled = window.pageYOffset || document.documentElement.scrollTop;
        const centerY = rect.top + scrolled + (rect.height / 2);
        const windowHeight = window.innerHeight;
        const fromCenter = (centerY - (scrolled + windowHeight / 2)) * this.parallaxSpeed;
        
        // Calculate a more limited parallax effect to prevent images from moving too far
        this.targetParallaxY = Math.min(Math.max(fromCenter, -rect.height * 0.3), rect.height * 0.3);
        this.parallaxY += (this.targetParallaxY - this.parallaxY) * 0.1;
        
        // Apply the transform to the proper wrapper element
        const targetElement = this.wrappers.parallax || this.wrappers.main;
        if (targetElement) {
            // Use matrix3d for better performance and to prevent flickering
            targetElement.style.transform = `translate3d(0, ${this.parallaxY}px, 0)`;
        }
        
        this.raf = requestAnimationFrame(this.startParallax.bind(this));
    }

    /**
     * Stop parallax animation
     */
    stopParallax() {
        if (this.raf) {
            cancelAnimationFrame(this.raf);
            this.raf = null;
        }
    }

    /**
     * Initialize mouse tracking for cursor-based interactions
     */
    initMouseTracking() {
        const handleMouseMove = (event) => {
            this.mouseX = event.clientX;
            this.mouseY = event.clientY;

            const rect = this.$el.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            // Calculate distance from center as a percentage
            const deltaX = (this.mouseX - centerX) / rect.width;
            const deltaY = (this.mouseY - centerY) / rect.height;
            
            // Determine strength based on distance from center
            const strength = 20; // Default strength
            
            // Set CSS variables for cursor repel effect on the interaction wrapper or fallback to main
            const targetElement = this.wrappers.interaction || this.$el;
            targetElement.style.setProperty('transition', 'transform 6s ease-out');
            targetElement.style.setProperty('--repel-x', `${(deltaX * strength) * -1}px`);
            targetElement.style.setProperty('--repel-y', `${(deltaY * strength) * -1}px`);
        };


        const container = this.$el.closest('.Pegasus__Container') || this.$el.parentElement;
        if (container) {
            container.addEventListener('mousemove', handleMouseMove);
            container.addEventListener('mouseleave', () => {
                // Reset transforms on mouse leave
                const targetElement = this.wrappers.interaction || this.$el;
                targetElement.style.setProperty('--repel-x', '0px');
                targetElement.style.setProperty('--repel-y', '0px');
            });
        }
    }
}

/**
 * Register the Animation component with Alpine.js
 */
document.addEventListener("alpine:init", () => {
    Alpine.data("animation", (isPreview) => 
        new Animation(isPreview)
    );
});
